import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Alert from '@/components/base/Alert';
import StatusBadge from '@/components/base/StatusBadge';
import { adminApi } from '@/lib/adminApi';
import type { AdminClient, AdminIpBlock, AdminTransfer, AdminValidationCode } from '@/lib/adminApi';
import { formatDate, formatMoney } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, Modal } from './ui';

type DeleteType = 'client' | 'transfer' | 'code' | 'ipBlock';

interface PendingDelete {
  type: DeleteType;
  id: string;
  label: string;
}

export default function DataTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [transfers, setTransfers] = useState<AdminTransfer[]>([]);
  const [codes, setCodes] = useState<AdminValidationCode[]>([]);
  const [blocks, setBlocks] = useState<AdminIpBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [section, setSection] = useState<DeleteType>('client');
  const [pending, setPending] = useState<PendingDelete | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [c, w, v, i] = await Promise.all([
        adminApi.clientsList(token),
        adminApi.transfersList(token),
        adminApi.codesList(token),
        adminApi.ipsList(token),
      ]);
      if (!c.ok || !w.ok || !v.ok || !i.ok) {
        setFailed(true);
        return;
      }
      setClients((c.clients ?? []) as AdminClient[]);
      setTransfers((w.transfers ?? []) as AdminTransfer[]);
      setCodes((v.codes ?? []) as AdminValidationCode[]);
      setBlocks((i.blocks ?? []) as AdminIpBlock[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    if (!pending || busy) return;
    setBusy(true);
    const res = await adminApi.dataDelete(token, pending.type, pending.id);
    setBusy(false);
    if (res.ok) {
      setMessage(t('admin.data.deleted'));
      setPending(null);
      load();
    }
  };

  const sections: { key: DeleteType; label: string; icon: string; count: number }[] = [
    { key: 'client', label: t('admin.data.clients'), icon: 'ri-group-line', count: clients.length },
    { key: 'transfer', label: t('admin.data.transfers'), icon: 'ri-exchange-dollar-line', count: transfers.length },
    { key: 'code', label: t('admin.data.codes'), icon: 'ri-shield-keyhole-line', count: codes.length },
    { key: 'ipBlock', label: t('admin.data.ips'), icon: 'ri-forbid-line', count: blocks.length },
  ];

  const renderList = () => {
    if (section === 'client') {
      if (clients.length === 0) return <EmptyBlock label={t('admin.data.empty')} icon="ri-group-line" />;
      return (
        <ul className="divide-y divide-background-200">
          {clients.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-foreground-950">{row.fullName}</p>
                <p className="text-[11.5px] text-foreground-500">
                  {row.email ?? '—'} · {formatMoney(row.balance, row.currency, i18n.language)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPending({ type: 'client', id: row.id, label: row.fullName })}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-accent-700 hover:border-accent-400 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-3.5 h-3.5">
                  <i className="ri-delete-bin-line text-[13px]" aria-hidden="true" />
                </span>
                {t('admin.data.delete')}
              </button>
            </li>
          ))}
        </ul>
      );
    }

    if (section === 'transfer') {
      if (transfers.length === 0) return <EmptyBlock label={t('admin.data.empty')} icon="ri-exchange-dollar-line" />;
      return (
        <ul className="divide-y divide-background-200">
          {transfers.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-foreground-950 font-label">{row.reference}</p>
                <p className="text-[11.5px] text-foreground-500">
                  {row.beneficiaryName} · {formatMoney(row.amount, row.currency, i18n.language)} ·{' '}
                  {formatDate(row.createdAt, i18n.language)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={row.status} />
                <button
                  type="button"
                  onClick={() => setPending({ type: 'transfer', id: String(row.id), label: row.reference })}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-accent-700 hover:border-accent-400 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('admin.data.delete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      );
    }

    if (section === 'code') {
      if (codes.length === 0) return <EmptyBlock label={t('admin.data.empty')} icon="ri-shield-keyhole-line" />;
      return (
        <ul className="divide-y divide-background-200">
          {codes.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
              <div className="min-w-0">
                <p className="font-label text-[12.5px] font-semibold tracking-[0.14em] text-foreground-950">
                  {row.code}
                </p>
                <p className="text-[11.5px] text-foreground-500">
                  {t(`admin.codes.type.${row.operationType}`)} · {row.operationRef ?? '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPending({ type: 'code', id: String(row.id), label: row.code })}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-accent-700 hover:border-accent-400 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('admin.data.delete')}
              </button>
            </li>
          ))}
        </ul>
      );
    }

    if (blocks.length === 0) return <EmptyBlock label={t('admin.data.empty')} icon="ri-forbid-line" />;
    return (
      <ul className="divide-y divide-background-200">
        {blocks.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
            <div className="min-w-0">
              <p className="font-label text-[12.5px] font-semibold text-foreground-950">{row.ip}</p>
              <p className="text-[11.5px] text-foreground-500">{row.reason ?? '—'}</p>
            </div>
            <button
              type="button"
              onClick={() => setPending({ type: 'ipBlock', id: String(row.id), label: row.ip })}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-accent-700 hover:border-accent-400 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('admin.data.delete')}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  if (loading) return <LoadingBlock />;
  if (failed) return <ErrorBlock onRetry={load} />;

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <AdminCard
        title={t('admin.data.title')}
        subtitle={t('admin.data.subtitle')}
        actions={
          <div className="inline-flex items-center p-1 rounded-full border border-background-300 bg-background-100 overflow-x-auto">
            {sections.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setSection(item.key)}
                className={`h-7 px-3 rounded-full text-[11.5px] font-semibold font-label whitespace-nowrap transition-colors cursor-pointer ${
                  section === item.key
                    ? 'bg-background-50 text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {item.label} · {item.count}
              </button>
            ))}
          </div>
        }
        padded={false}
      >
        {renderList()}
      </AdminCard>

      <Modal
        open={Boolean(pending)}
        title={t('admin.data.confirmTitle')}
        subtitle={pending?.label}
        onClose={() => setPending(null)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setPending(null)}>
              {t('admin.common.cancel')}
            </Button>
            <Button size="sm" loading={busy} onClick={confirmDelete}>
              {t('admin.data.delete')}
            </Button>
          </>
        }
      >
        <Alert tone="error" title={t('admin.data.confirmTitle')}>
          {t('admin.data.confirmText')}
        </Alert>
      </Modal>
    </div>
  );
}